// FOS-CATALOG-VIBE-TAGS-001 — the only place vibe tags are defined.
//
// Every tag id, its display label, and the deterministic derivation rule live
// here. The SQL check constraint on fragrance.catalog_vibe_tags mirrors
// VIBE_TAG_IDS; ops/vibe-tags-lane/sql-migration.test.mjs asserts the two lists
// are identical, so adding a tag means a migration as well as an edit here.
// No free-text tags anywhere: the UI offers this list, the lane writes this
// list, the RPC rejects anything else.
//
// Rule shape: each signal group maps a keyword to a weight. Positive weights
// raise confidence, negative weights lower it. Confidence is the clamped sum
// of matched weights (each keyword counts once per group), so a fragrance with
// amber, vanilla and tobacco accords saturates "sexy" while a citrus-aquatic
// row is pushed to zero by the negative weights. Keywords match whole words
// inside a note, accord, family, occasion or season string ("warm spicy"
// matches "spicy" but "watermelon" does not match "water").
//
// Signals read from the catalog row only (AGENTS.md rule 5): top/mid/base/all
// notes, accord_tags, scent_family, occasions, seasons, projection text, and
// the projection / longevity / sillage scores. Nothing is recalled about a
// product by name.

// Browser-safe: this file is bundled into the app for labels and the correction
// sheet, so it imports nothing from node. The lane hashes the rule input.

const T =(id, label, rules) => Object.freeze({ id, label, rules: Object.freeze(rules) })

export const RULE_VERSION = 'rule:v1'
export const MODEL_TIE_BREAK_LOW = 0.4
export const MODEL_TIE_BREAK_HIGH = 0.6
export const DISPLAY_THRESHOLD = 0.6

export const VIBE_TAGS = Object.freeze([
  T('sexy', 'Sexy', {
    accords: { amber: 0.25, vanilla: 0.2, oud: 0.15, musky: 0.15, tobacco: 0.2, 'warm spicy': 0.15, animalic: 0.2, leather: 0.1, aquatic: -0.3, citrus: -0.15, fresh: -0.2, green: -0.15 },
    notes: { vanilla: 0.2, tobacco: 0.2, amber: 0.15, tonka: 0.15, benzoin: 0.1, oud: 0.15, musk: 0.1, 'sea notes': -0.2 },
    occasions: { 'date night': 0.25, evening: 0.15, 'night out': 0.15, office: -0.1 },
  }),
  T('clean', 'Clean', {
    accords: { fresh: 0.2, citrus: 0.15, aquatic: 0.2, soapy: 0.3, 'white floral': 0.1, musky: 0.1, powdery: 0.1, oud: -0.3, tobacco: -0.3, animalic: -0.3, smoky: -0.3, amber: -0.2, boozy: -0.2 },
    notes: { bergamot: 0.1, cotton: 0.2, aldehydes: 0.15, 'white musk': 0.25, soap: 0.3, iris: 0.05, 'sea notes': 0.1 },
    families: { fresh: 0.15, citrus: 0.15, aquatic: 0.15 },
    occasions: { office: 0.15, daily: 0.1 },
  }),
  T('fresh', 'Fresh', {
    accords: { fresh: 0.3, citrus: 0.3, aquatic: 0.3, green: 0.2, aromatic: 0.15, 'fresh spicy': 0.15, amber: -0.25, vanilla: -0.2, tobacco: -0.3, oud: -0.2, 'warm spicy': -0.15 },
    notes: { bergamot: 0.15, lemon: 0.15, grapefruit: 0.15, 'sea notes': 0.2, mint: 0.15, lime: 0.1 },
    families: { fresh: 0.2, citrus: 0.2, aquatic: 0.2 },
    seasons: { summer: 0.1, spring: 0.05 },
  }),
  T('cozy', 'Cozy', {
    accords: { vanilla: 0.25, amber: 0.2, 'warm spicy': 0.2, sweet: 0.15, powdery: 0.1, tobacco: 0.15, woody: 0.05, aquatic: -0.3, citrus: -0.15, fresh: -0.2, green: -0.15 },
    notes: { vanilla: 0.2, tonka: 0.2, cinnamon: 0.15, benzoin: 0.15, sandalwood: 0.1, cashmeran: 0.15, praline: 0.1 },
    seasons: { winter: 0.15, fall: 0.1 },
  }),
  T('boozy', 'Boozy', {
    accords: { boozy: 0.5, rum: 0.4, whiskey: 0.4, cognac: 0.4 },
    notes: { rum: 0.35, whiskey: 0.35, cognac: 0.35, bourbon: 0.3, wine: 0.2, brandy: 0.3, davana: 0.1 },
  }),
  T('sweet', 'Sweet', {
    accords: { sweet: 0.4, vanilla: 0.2, caramel: 0.3, gourmand: 0.2, fruity: 0.1, green: -0.1, aromatic: -0.1 },
    notes: { vanilla: 0.15, caramel: 0.25, praline: 0.25, honey: 0.2, tonka: 0.1, sugar: 0.3 },
  }),
  T('gourmand', 'Gourmand', {
    accords: { gourmand: 0.45, caramel: 0.3, chocolate: 0.3, sweet: 0.15, vanilla: 0.15, coffee: 0.2, aromatic: -0.1, green: -0.15 },
    notes: { chocolate: 0.3, caramel: 0.3, praline: 0.3, coffee: 0.2, almond: 0.15, pistachio: 0.2, marshmallow: 0.3, cake: 0.3 },
    families: { gourmand: 0.3 },
  }),
  T('smoky', 'Smoky', {
    accords: { smoky: 0.5, tobacco: 0.2, leather: 0.15, oud: 0.1, incense: 0.2, aquatic: -0.2, citrus: -0.1 },
    notes: { incense: 0.2, birch: 0.3, smoke: 0.4, 'guaiac wood': 0.2, tobacco: 0.15, oud: 0.1, cade: 0.3 },
  }),
  T('smell_like_a_boss', 'Smell Like a Boss', {
    accords: { amber: 0.15, spicy: 0.2, 'warm spicy': 0.2, leather: 0.2, woody: 0.1, tobacco: 0.2, oud: 0.2 },
    notes: { amber: 0.1, pepper: 0.1, cardamom: 0.1, leather: 0.15, cedar: 0.1, tobacco: 0.15, oud: 0.15, saffron: 0.1 },
    occasions: { evening: 0.15, office: 0.2, work: 0.2 },
  }),
  T('beast_mode', 'Beast Mode', {
    accords: { amber: 0.1, spicy: 0.1, 'warm spicy': 0.1, smoky: 0.1, tobacco: 0.1, leather: 0.1, oud: 0.1, animalic: 0.1 },
    notes: { oud: 0.1, tobacco: 0.1, leather: 0.1, incense: 0.1, amber: 0.1, patchouli: 0.05, birch: 0.05, cade: 0.1, saffron: 0.05 },
    occasions: { evening: 0.05, 'night out': 0.1, club: 0.1, clubbing: 0.1 },
    seasons: { winter: 0.05 },
    projection: { strong: 0.3, enormous: 0.4, heavy: 0.3, soft: -0.4, intimate: -0.4, moderate: -0.1 },
    scores: { projection_score: [8, 0.3], longevity_score: [8, 0.3], sillage_score: [8, 0.2] },
  }),
  T('office_safe', 'Office safe', {
    accords: { fresh: 0.1, citrus: 0.1, soapy: 0.1, powdery: 0.05, oud: -0.2, animalic: -0.3, smoky: -0.2, boozy: -0.1 },
    occasions: { office: 0.35, work: 0.35, daily: 0.15 },
    projection: { soft: 0.1, moderate: 0.1, strong: -0.2, enormous: -0.3 },
    scores: { sillage_score: [8, -0.2] },
  }),
  T('day', 'Day', {
    accords: { citrus: 0.15, fresh: 0.15, aquatic: 0.1, green: 0.1, oud: -0.1, boozy: -0.1 },
    occasions: { daily: 0.3, day: 0.3, daytime: 0.3, office: 0.2, casual: 0.2, 'night out': -0.2 },
  }),
  T('evening', 'Evening', {
    accords: { amber: 0.1, oud: 0.1, 'warm spicy': 0.1, boozy: 0.1, vanilla: 0.05, aquatic: -0.15, citrus: -0.1 },
    occasions: { evening: 0.4, night: 0.3, 'date night': 0.2, formal: 0.15, office: -0.15 },
  }),
  T('date_night', 'Date night', {
    accords: { vanilla: 0.1, amber: 0.1, sweet: 0.1, musky: 0.1, aquatic: -0.2 },
    occasions: { 'date night': 0.5, romantic: 0.4, date: 0.4, evening: 0.15, office: -0.1 },
  }),
  T('night_out', 'Night out', {
    accords: { sweet: 0.1, amber: 0.1, boozy: 0.1, oud: 0.05 },
    occasions: { 'night out': 0.5, club: 0.4, clubbing: 0.4, party: 0.4, evening: 0.1, office: -0.2 },
    projection: { strong: 0.1 },
  }),
  T('summer', 'Summer', {
    accords: { citrus: 0.2, aquatic: 0.25, fresh: 0.15, fruity: 0.1, green: 0.1, amber: -0.15, tobacco: -0.2, oud: -0.15 },
    notes: { coconut: 0.2 },
    seasons: { summer: 0.5, winter: -0.2 },
  }),
  T('winter', 'Winter', {
    accords: { amber: 0.2, 'warm spicy': 0.2, vanilla: 0.15, oud: 0.15, tobacco: 0.15, boozy: 0.15, aquatic: -0.25, citrus: -0.15 },
    seasons: { winter: 0.5, summer: -0.2 },
  }),
  T('green', 'Green', {
    accords: { green: 0.5, herbal: 0.2, aromatic: 0.1, earthy: 0.1, amber: -0.1, vanilla: -0.15 },
    notes: { vetiver: 0.15, galbanum: 0.3, 'violet leaf': 0.3, basil: 0.2, grass: 0.3, 'fig leaf': 0.2, 'tomato leaf': 0.3 },
    families: { green: 0.3 },
  }),
  T('aquatic', 'Aquatic', {
    accords: { aquatic: 0.5, marine: 0.5, ozonic: 0.3, salty: 0.15, tobacco: -0.2, oud: -0.2, vanilla: -0.1 },
    notes: { 'sea notes': 0.4, calone: 0.4, 'sea salt': 0.3, water: 0.2, seaweed: 0.2, 'sea water': 0.4 },
    families: { aquatic: 0.3 },
  }),
  T('powdery', 'Powdery', {
    accords: { powdery: 0.5, iris: 0.3, violet: 0.2, musky: 0.05, aquatic: -0.1 },
    notes: { iris: 0.3, orris: 0.3, violet: 0.2, heliotrope: 0.3, rice: 0.2, talc: 0.3 },
  }),
  T('leathery', 'Leathery', {
    accords: { leather: 0.55, animalic: 0.15, smoky: 0.1, aquatic: -0.2, citrus: -0.1 },
    notes: { leather: 0.45, suede: 0.4, saffron: 0.1, birch: 0.1, styrax: 0.1 },
  }),
  T('spicy', 'Spicy', {
    accords: { 'warm spicy': 0.4, 'fresh spicy': 0.35, spicy: 0.4, cinnamon: 0.2, aquatic: -0.15 },
    notes: { pepper: 0.2, cardamom: 0.2, cinnamon: 0.2, clove: 0.2, nutmeg: 0.2, saffron: 0.15, ginger: 0.15, 'pink pepper': 0.15 },
  }),
  T('warm', 'Warm', {
    accords: { 'warm spicy': 0.3, amber: 0.3, vanilla: 0.2, woody: 0.1, tobacco: 0.1, balsamic: 0.2, aquatic: -0.3, citrus: -0.2, fresh: -0.2, green: -0.1 },
    notes: { amber: 0.2, benzoin: 0.15, labdanum: 0.2, sandalwood: 0.1, tonka: 0.15 },
    seasons: { winter: 0.05, fall: 0.05 },
  }),
  T('crowd_pleaser', 'Crowd pleaser', {
    accords: { sweet: 0.15, fruity: 0.1, vanilla: 0.1, fresh: 0.1, citrus: 0.1, musky: 0.05, animalic: -0.3, smoky: -0.15, oud: -0.1, leather: -0.1 },
    occasions: { daily: 0.1 },
    scores: { fragrantica_rating: [4.2, 0.25] },
  }),
  T('niche_leaning', 'Niche leaning', {
    accords: { oud: 0.2, incense: 0.2, animalic: 0.2, smoky: 0.15, leather: 0.1, earthy: 0.15, metallic: 0.2, mineral: 0.2, fruity: -0.15, sweet: -0.15, gourmand: -0.1 },
    notes: { oud: 0.2, incense: 0.15, saffron: 0.1, cade: 0.1 },
  }),
  T('mature', 'Mature', {
    accords: { chypre: 0.3, oakmoss: 0.3, animalic: 0.2, leather: 0.15, powdery: 0.1, aldehydic: 0.2, tobacco: 0.1, fruity: -0.2, sweet: -0.15, gourmand: -0.2 },
    notes: { oakmoss: 0.3, patchouli: 0.1, civet: 0.3, castoreum: 0.3, aldehydes: 0.2, vetiver: 0.05 },
    families: { chypre: 0.3 },
  }),
  T('youthful', 'Youthful', {
    accords: { fruity: 0.3, sweet: 0.25, gourmand: 0.15, citrus: 0.1, fresh: 0.1, oakmoss: -0.25, chypre: -0.25, animalic: -0.25, leather: -0.15, aldehydic: -0.15 },
    notes: { candy: 0.3, 'cotton candy': 0.3, strawberry: 0.2, raspberry: 0.2, apple: 0.15, bubblegum: 0.3, pear: 0.1 },
  }),
])

export const VIBE_TAG_IDS = Object.freeze(VIBE_TAGS.map(tag => tag.id))
const TAG_BY_ID = new Map(VIBE_TAGS.map(tag => [tag.id, tag]))

export function isVibeTag(id) {
  return TAG_BY_ID.has(id)
}

export function vibeTagLabel(id) {
  return TAG_BY_ID.get(id)?.label || null
}

// Creator-source phrase table (directive item 7). A YouTube title or a
// description heading naming one of these phrases tags every fragrance in the
// video's resolved featured list. Longer phrases are listed before their
// substrings so "beast mode" wins over nothing and "sexiest" is matched as a
// whole word rather than as "sexy" + suffix.
export const CREATOR_PHRASES = Object.freeze([
  Object.freeze({ phrase: 'smell like a boss', tag: 'smell_like_a_boss' }),
  Object.freeze({ phrase: 'beast mode', tag: 'beast_mode' }),
  Object.freeze({ phrase: 'date night', tag: 'date_night' }),
  Object.freeze({ phrase: 'sexiest', tag: 'sexy' }),
  Object.freeze({ phrase: 'sexy', tag: 'sexy' }),
  Object.freeze({ phrase: 'clean', tag: 'clean' }),
  Object.freeze({ phrase: 'freshies', tag: 'fresh' }),
  Object.freeze({ phrase: 'fresh', tag: 'fresh' }),
  Object.freeze({ phrase: 'cozy', tag: 'cozy' }),
  Object.freeze({ phrase: 'office', tag: 'office_safe' }),
  Object.freeze({ phrase: 'compliment', tag: 'crowd_pleaser' }),
])

function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const PHRASE_PATTERNS = CREATOR_PHRASES.map(({ phrase, tag }) => ({
  tag,
  phrase,
  // "compliment" also covers "compliments"/"complimented"; every other phrase is exact.
  pattern: new RegExp(`\\b${escapeRegex(phrase)}${phrase === 'compliment' ? '\\w*' : ''}\\b`, 'i'),
}))

// A description heading is the first line, or any line that reads like a
// heading: short, no sentence punctuation, optionally followed by a colon.
function descriptionHeadings(description) {
  return String(description || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter((line, index) => line && (index === 0 || (line.length <= 80 && !/[.!?]$/.test(line))))
}

export function matchCreatorPhrases({ title, description }) {
  const haystacks = [String(title || ''), ...descriptionHeadings(description)]
  const hits = new Map()
  for (const text of haystacks) {
    for (const { tag, phrase, pattern } of PHRASE_PATTERNS) {
      if (!hits.has(tag) && pattern.test(text)) hits.set(tag, { tag, phrase, matched_text: text.slice(0, 160) })
    }
  }
  return [...hits.values()]
}

// ── Deterministic rule application ──────────────────────────────────────────

function normalizeList(value) {
  if (Array.isArray(value)) return value.map(item => String(item || '').trim().toLowerCase()).filter(Boolean)
  if (typeof value === 'string') return value.split(/[,;|/]+/).map(item => item.trim().toLowerCase()).filter(Boolean)
  return []
}

function wordMatch(keyword, text) {
  return new RegExp(`(^|[^a-z])${escapeRegex(keyword)}([^a-z]|$)`, 'i').test(text)
}

function matchGroup(group, values, signal, matched) {
  let total = 0
  if (!group) return total
  for (const [keyword, weight] of Object.entries(group)) {
    const hit = values.find(value => wordMatch(keyword, value))
    if (hit === undefined) continue
    matched.push({ signal, keyword, value: hit, weight })
    total += weight
  }
  return total
}

// The exact inputs the rules read, normalised. This is also what the model
// sees, and its SHA-256 is what makes re-runs idempotent.
export function vibeRuleInput(row) {
  return {
    notes: [...new Set([
      ...normalizeList(row.top_notes),
      ...normalizeList(row.mid_notes),
      ...normalizeList(row.base_notes),
      ...normalizeList(row.all_notes),
    ])],
    accords: [...new Set(normalizeList(row.accord_tags))],
    family: [...new Set(normalizeList(row.scent_family))],
    occasions: [...new Set(normalizeList(row.occasions))],
    seasons: [...new Set(normalizeList(row.seasons))],
    projection: String(row.projection || '').trim().toLowerCase() || null,
    scores: {
      projection_score: row.projection_score == null ? null : Number(row.projection_score),
      longevity_score: row.longevity_score == null ? null : Number(row.longevity_score),
      sillage_score: row.sillage_score == null ? null : Number(row.sillage_score),
      fragrantica_rating: row.fragrantica_rating == null ? null : Number(row.fragrantica_rating),
    },
  }
}

// True when the row carries something the rules can read. With nothing to
// ground on, neither the rule table nor the model runs (AGENTS.md rule 5).
export function hasGroundingData(input) {
  return input.notes.length > 0 || input.accords.length > 0 || input.family.length > 0
}

export function deriveVibeTags(row) {
  const input = vibeRuleInput(row)
  if (!hasGroundingData(input)) return { input, tags: [] }
  const tags = []
  for (const tag of VIBE_TAGS) {
    const matched = []
    let score = 0
    score += matchGroup(tag.rules.accords, input.accords, 'accord', matched)
    score += matchGroup(tag.rules.notes, input.notes, 'note', matched)
    score += matchGroup(tag.rules.families, input.family, 'family', matched)
    score += matchGroup(tag.rules.occasions, input.occasions, 'occasion', matched)
    score += matchGroup(tag.rules.seasons, input.seasons, 'season', matched)
    if (tag.rules.projection && input.projection) {
      score += matchGroup(tag.rules.projection, [input.projection], 'projection', matched)
    }
    for (const [field, [threshold, weight]] of Object.entries(tag.rules.scores || {})) {
      const value = input.scores[field]
      if (value != null && Number.isFinite(value) && value >= threshold) {
        matched.push({ signal: 'score', keyword: field, value, weight })
        score += weight
      }
    }
    if (matched.length === 0) continue
    const confidence = Math.min(1, Math.max(0, Math.round(score * 1000) / 1000))
    tags.push({ tag: tag.id, confidence, matched })
  }
  return { input, tags }
}

export function needsModelTieBreak(confidence) {
  return confidence >= MODEL_TIE_BREAK_LOW && confidence < MODEL_TIE_BREAK_HIGH
}
